import type { SyntheticEvent } from 'react'
import React, { useState } from 'react'

import { Box, DisplayPosition, Modal } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import { ScrollableBasketModalContent } from '@features/scrollable-basket'
import type { Recipe, RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useBasket } from 'routes/Menu/domains/basket'
import { usePricing } from 'routes/Menu/domains/pricing'
import {
  getBasketDate,
  getBasketMenuUuid,
  getBasketRecipePositions,
  getChosenAddress,
  getSelectedDeliverySlot,
} from 'selectors/basket'
import { getUserFirstName } from 'selectors/user'

import { RecipeDetail } from '../Search/RecipeDetail/RecipeDetail'

type ShowBasketEvent = {
  action: 'show_basket'
  properties: {
    menu_uuid: string
    basket_recipe_count: number
    recipes_in_basket: string
  }
}

type ViewBasketModalProps = {
  onCloseModal: () => void
  onCheckout: (e: SyntheticEvent) => void
}

export const ViewBasketModal = ({ onCloseModal, onCheckout }: ViewBasketModalProps) => {
  const { basketRecipes } = useBasketRecipes()
  const recipePositions = useSelector(getBasketRecipePositions)
  const { numPortions } = useNumPortions()
  const maxRecipesInBox = useMaxRecipesForPortion(numPortions)
  const { addRecipe, removeRecipe } = useBasket()
  const { pricing } = usePricing()

  const user = useSelector(getUserFirstName)
  const basketDate = useSelector(getBasketDate)
  const address = useSelector(getChosenAddress)
  const slot = useSelector(getSelectedDeliverySlot)
  const menuUuid = useSelector(getBasketMenuUuid)
  const [recipeDetailsId, setRecipeDetailsId] = useState<RecipeUUID | null>(null)

  const validBasketRecipes = basketRecipes
    .filter((br): br is typeof br & { recipe: Recipe } => br.recipe != null)
    .sort((a, b) => {
      const posA = recipePositions?.indexOf(a.recipe.coreId) ?? -1
      const posB = recipePositions?.indexOf(b.recipe.coreId) ?? -1

      return posA - posB
    })

  const deliverySlot = {
    date: basketDate,
    timeStart: slot?.get('deliveryStartTime'),
    timeEnd: slot?.get('deliveryEndTime'),
  }

  const deliveryAddress = {
    line1: address?.get('line1') ?? '',
    line2: address?.get('line2') ?? '',
    line3: address?.get('line3') ?? '',
    town: address?.get('town') ?? '',
    postcode: address?.get('postcode') ?? '',
    state: address?.get('state') ?? '',
    county: address?.get('county') ?? '',
  }

  const deliveryPricing = {
    recipeTotal: `£${pricing?.recipeTotal ?? '--'}`,
    discountTotal: `£${pricing?.totalDiscount ?? '--'}`,
    percentageOff: `${Number(pricing?.percentageOff || 0).toFixed(0)}%`,
    surchargeTotal: (pricing?.surchargeCount || 0) > 0 ? `£${pricing?.surchargeTotal}` : undefined,
    finalTotal: `£${pricing?.total ?? '--'}`,
    deliveryTotal: pricing?.isDeliveryFree ? 'Free' : `£${pricing?.deliveryTotal ?? '--'}`,
  }

  const trackModalOpen = () => {
    const validRecipesInBasketUuids = validBasketRecipes.flatMap((r) =>
      Array(r.quantity).fill(r.recipe.id),
    )

    queueTrackingEvent<ShowBasketEvent>({
      action: 'show_basket',
      properties: {
        menu_uuid: menuUuid,
        basket_recipe_count: validRecipesInBasketUuids.length,
        recipes_in_basket: validRecipesInBasketUuids.join(','),
      },
    })
  }

  return (
    <Modal isOpen name="view-basket-modal" onAfterClose={onCloseModal} onAfterOpen={trackModalOpen}>
      {recipeDetailsId ? (
        <RecipeDetail onClose={() => setRecipeDetailsId(null)} recipeId={recipeDetailsId} />
      ) : (
        <Box style={{ paddingTop: '1.2rem' }} position={DisplayPosition.Relative}>
          <ScrollableBasketModalContent
            name={user}
            maxRecipesInBox={maxRecipesInBox}
            basketRecipes={validBasketRecipes}
            closeModal={onCloseModal}
            addRecipe={addRecipe}
            removeRecipe={removeRecipe}
            onClickRecipe={(recipeUuid) => setRecipeDetailsId(recipeUuid)}
            onCheckout={onCheckout}
            deliverySlot={deliverySlot}
            deliveryAddress={deliveryAddress}
            deliveryPricing={deliveryPricing}
          />
        </Box>
      )}
    </Modal>
  )
}
